
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import ModelGenerationScreen from './components/StartScreen';
import ProfilePage from './pages/ProfilePage';
import MyStylesPage from './pages/MyStylesPage';
import ServicesPage from './pages/ServicesPage';
import PortfolioPage from './pages/PortfolioPage';
import BlogPage from './pages/BlogPage';
import AboutPage from './pages/AboutPage';
import LegalPage from './pages/LegalPage';
import HelpPage from './pages/HelpPage';
import StylingAdvisorPage from './pages/StylingAdvisorPage';
import BookingPage from './pages/BookingPage';
import AuthorPage from './pages/AuthorPage';
import BlogPostPage from './pages/BlogPostPage';
import DeploymentGuidePage from './pages/DeploymentGuidePage';
import GuestAccessDeniedPage from './pages/GuestAccessDeniedPage';
import StyleReportPage from './pages/StyleReportPage';
import PressPage from './pages/PressPage';
import TravelPage from './pages/TravelPage';
import ContactPage from './pages/ContactPage';
import TrendsPage from './pages/TrendsPage';
import ColorStudioPage from './pages/ColorStudioPage';
import CapsuleGeneratorPage from './pages/CapsuleGeneratorPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ServiceSelectionWizardPage from './pages/ServiceSelectionWizardPage';
import DigitalBoxRequestPage from './pages/DigitalBoxRequestPage';
import TryOnPage from './pages/TryOnPage';
import StyleLabPage from './pages/StyleLabPage';
import Canvas from './components/Canvas';
import { ProtectedRoute } from './components/ProtectedRoute';


import { useAppContext } from './AppContext';
import { OutfitLayer, WardrobeItem, UserProfile, SavedContent, StyleReport } from './types';
import { VIEW_360_POSES } from './constants';

interface AppRoutesProps {
    onPhotoUpload: (file: File) => void;
    onGenerateModel: () => void;
    handleModelFinalized: (url: string, profile?: Partial<UserProfile>) => void;
    handleNavigate: (page: any, params?: any) => void;
    currentLayer: OutfitLayer;
    baseModelImageUrl: string | null;
    isLoading: boolean;
    loadingMessage: string;
    loadingAction: string | null;
    error: string | null;
    onClearError: () => void;
    handlePoseSelect: (pose: string) => void;
    POSE_CATEGORIES: any;
    onShopTheLook: () => void;
    currentOutfitId: string | null;
    onSaveOutfit: () => void;
    onStartOver: () => void;
    handleSceneUpdate: (config: any) => void;
    handleGenerate3DViews: () => Promise<boolean>;
    handleToggleFavoritePose: (pose: string) => void;
    onDeleteContent: (id: string) => void;
    onLoadOutfit: (o: any) => void | Promise<void>;
    onDeleteOutfit: (id: string) => void | Promise<void>;
    onShowLookbookCreator: () => void;
    handleShareContent: (content: any) => void;
    pageParams: Record<string, string>;
    onRestartOnboarding: () => void;
    allUsers: string[];
    onCreateNewUser: (name: string) => void;
    onDeleteUser: (userId: string) => void;
    onChangeUser: (userId: string) => Promise<void>;
    onOpenVoucherModal: () => void;
    onOpenAuthModal: () => void;
    onSaveContent: (contentToSave: Omit<SavedContent, 'id' | 'createdAt'>) => void;
    onSaveReport?: (report: any, imageUrl: string) => void;
    styleReport: StyleReport | null;
    onCloseReport: () => void;
    onGenerateReport: () => void;
    onCreativeEdit?: (type: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => Promise<void>;
    wardrobe: WardrobeItem[];
    setWardrobe: React.Dispatch<React.SetStateAction<WardrobeItem[]>>;
    onItemAdd?: { item: WardrobeItem; timestamp: number } | null;
    location?: any;
}

export const AppRoutes: React.FC<AppRoutesProps> = (props) => {
    const {
        userProfile, setUserProfile, currentUserId, savedOutfits, lookbooks, savedContent,
        favoritedOutfitIds, toggleFavoriteOutfit, updateUserProfile, wardrobe,
        setWardrobe
    } = useAppContext();

    const isGuest = currentUserId.includes('guest');

    return (
        <Routes location={props.location} key={props.location?.pathname}>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={
                <HomePage
                    onPhotoUpload={props.onPhotoUpload}
                    onNavigate={props.handleNavigate}
                    onGenerateModel={props.onGenerateModel}
                    isModelLoaded={!!props.baseModelImageUrl}
                    onModelFinalized={props.handleModelFinalized}
                    isLoading={props.isLoading}
                    loadingMessage={props.loadingMessage}
                />
            } />
            <Route path="/studio" element={<ProtectedRoute><StyleLabPage {...props} onClearError={props.onClearError} userProfile={userProfile} currentUserId={currentUserId} onNavigate={props.handleNavigate} onItemAdd={props.onItemAdd} /></ProtectedRoute>} />
            <Route path="/try-on" element={<ProtectedRoute><StyleLabPage {...props} onClearError={props.onClearError} userProfile={userProfile} currentUserId={currentUserId} onNavigate={props.handleNavigate} onItemAdd={props.onItemAdd} /></ProtectedRoute>} />
            <Route path="/canvas" element={<ProtectedRoute><StyleLabPage {...props} onClearError={props.onClearError} userProfile={userProfile} currentUserId={currentUserId} onNavigate={props.handleNavigate} onItemAdd={props.onItemAdd} /></ProtectedRoute>} />
            <Route path="/style-lab" element={<ProtectedRoute><StyleLabPage {...props} onClearError={props.onClearError} userProfile={userProfile} currentUserId={currentUserId} onNavigate={props.handleNavigate} onItemAdd={props.onItemAdd} /></ProtectedRoute>} />
            <Route path="/old-canvas" element={
                <Canvas
                    displayImageUrl={props.currentLayer.poseOutputs[props.currentLayer.currentPoseInstruction] || props.baseModelImageUrl || null}
                    baseModelImageUrl={props.baseModelImageUrl}
                    isLoading={props.isLoading}
                    loadingMessage={props.loadingMessage}
                    loadingAction={props.loadingAction}
                    generationError={props.error}
                    onClearError={props.onClearError}
                    onSelectPose={props.handlePoseSelect}
                    poseCategories={props.POSE_CATEGORIES}
                    currentPoseInstruction={props.currentLayer.currentPoseInstruction}
                    availablePoseKeys={Object.keys(props.currentLayer.poseOutputs)}
                    onShopTheLook={props.onShopTheLook}
                    isShopTheLookEnabled={true}
                    isFavorited={props.currentOutfitId ? favoritedOutfitIds.has(props.currentOutfitId) : false}
                    onToggleFavorite={() => props.currentOutfitId && toggleFavoriteOutfit(props.currentOutfitId)}
                    isFavoriteEnabled={!!props.currentOutfitId}
                    userProfile={userProfile}
                    isComparisonMode={false}
                    onToggleComparisonMode={() => { }}
                    onSaveOutfit={props.onSaveOutfit}
                    onStartOver={props.onStartOver}
                    comparisonOutfits={null}
                    currentOutfit={props.currentLayer}
                    onUpdateSceneConfig={props.handleSceneUpdate}
                    onGenerate3DView={props.handleGenerate3DViews}
                    view360Images={VIEW_360_POSES.map(pose => props.currentLayer.poseOutputs[pose]).filter(Boolean) as string[]}
                    onToggleFavoritePose={props.handleToggleFavoritePose}
                    onNavigate={props.handleNavigate}
                    onApplyCreativeTool={props.onCreativeEdit}
                    onSaveReport={props.onSaveReport}
                    styleReport={props.styleReport}
                    onCloseReport={props.onCloseReport}
                    onGenerateReport={props.onGenerateReport}
                />
            } />
            <Route path="/profile" element={
                <ProfilePage
                    currentUserId={currentUserId}
                    savedOutfits={savedOutfits}
                    userProfile={userProfile}
                    setUserProfile={setUserProfile}
                    onSaveToCloud={async () => { }} // Mocked
                    onLoadFromCloud={async () => { }} // Mocked
                    allUsers={props.allUsers}
                    onDeleteUser={props.onDeleteUser}
                    onChangeUser={props.onChangeUser}
                    onRestartOnboarding={props.onRestartOnboarding}
                    onNavigate={props.handleNavigate}
                    onCreateNewUser={props.onCreateNewUser}
                    onOpenAuthModal={props.onOpenAuthModal}
                    wardrobe={props.wardrobe}
                    setWardrobe={props.setWardrobe}
                />
            } />
            <Route path="/my-styles" element={
                <MyStylesPage
                    savedOutfits={savedOutfits}
                    lookbooks={lookbooks}
                    savedContent={savedContent}
                    onLoadOutfit={props.onLoadOutfit as any}
                    onDeleteOutfit={props.onDeleteOutfit as any}
                    onDeleteContent={props.onDeleteContent}
                    onShowLookbookCreator={props.onShowLookbookCreator}
                    favoritedOutfitIds={favoritedOutfitIds}
                    onToggleFavorite={toggleFavoriteOutfit}
                    onNavigate={props.handleNavigate}
                    userProfile={userProfile}
                />
            } />
            <Route path="/styling-advisor" element={
                <StylingAdvisorPage
                    userProfile={userProfile}
                    savedOutfits={savedOutfits}
                    lookbooks={lookbooks}
                    savedContent={savedContent}
                    onSaveContent={props.onSaveContent}
                    onNavigate={props.handleNavigate}
                    setUserProfile={setUserProfile}
                    initialAgentId={props.pageParams.agent}
                    isGuest={isGuest}
                    onAddToBasket={(item) => props.handleShareContent({ type: 'search', title: item.name, content: item.description })}
                />
            } />
            <Route path="/services" element={<ServicesPage onNavigate={props.handleNavigate} onOpenVoucherModal={props.onOpenVoucherModal} />} />
            <Route path="/travel" element={<TravelPage />} />
            <Route path="/portfolio" element={<PortfolioPage onNavigate={props.handleNavigate} />} />
            <Route path="/about" element={<AboutPage onNavigate={props.handleNavigate} />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/blog-post/:postId" element={<BlogPostPage postId={props.pageParams.postId || ""} onNavigate={props.handleNavigate} />} />
            <Route path="/blog" element={<BlogPage onNavigate={props.handleNavigate} userProfile={userProfile} />} />
            <Route path="/help" element={<HelpPage onNavigate={props.handleNavigate} />} />
            <Route path="/legal" element={<LegalPage onNavigate={props.handleNavigate} />} />
            <Route path="/booking" element={<BookingPage onNavigate={props.handleNavigate} />} />
            <Route path="/bookings" element={<Navigate to="/booking" replace />} />
            <Route path="/trends" element={<TrendsPage onNavigate={props.handleNavigate} />} />
            <Route path="/contact" element={<ContactPage onNavigate={props.handleNavigate} />} />
            <Route path="/author/:authorId" element={<AuthorPage authorId={props.pageParams.authorId || ""} onNavigate={props.handleNavigate} />} />
            <Route path="/style-report" element={
                <StyleReportPage
                    userProfile={userProfile}
                    savedOutfits={savedOutfits}
                    lookbooks={lookbooks}
                    savedContent={savedContent}
                    currentUserId={currentUserId}
                    onNavigate={props.handleNavigate}
                />
            } />
            <Route path="/color-studio" element={<ColorStudioPage />} />
            <Route path="/capsule-generator" element={<CapsuleGeneratorPage />} />
            <Route path="/admin-dashboard" element={<AdminDashboardPage />} />
            <Route path="/service-selection" element={<ServiceSelectionWizardPage onNavigate={props.handleNavigate} />} />
            <Route path="/digital-box" element={<DigitalBoxRequestPage onNavigate={props.handleNavigate} />} />
            <Route path="/access-denied" element={<GuestAccessDeniedPage />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
    );
};
